/* METODIKA PLÁNU — razítko verze výpočtu u uloženého Návrhu řešení
 * ============================================================================
 * PRAVIDLO MAJITELE (3. 10. 2026, přestavba plánu, volba B): staré uložené
 * plány se NEPŘEPOČÍTÁVAJÍ. Plán, který poradce uložil a možná už ukázal
 * klientovi, drží svá čísla, dokud poradce sám neklikne „Přepočítat podle
 * nové metodiky“. Jinak by se klientovi pod rukama měnila čísla, která
 * viděl (kontrola Č20 4. 10. 2026: rezerva 101 % → 57 %, auto 4 166 →
 * 6 390 Kč/měs, renta +8 610 Kč/měs — bez jediného kliknutí).
 *
 * JAK SE TO POZNÁ
 *   · `navrh.metodika` = razítko (datum verze, „2026-10-04“). Dostane ho nový
 *     návrh při založení (uloží se s prvním uložením) a starý návrh kliknutím
 *     v pruhu „Přepočítat podle nové metodiky“.
 *   · Návrh, který UŽ BYL ULOŽENÝ a razítko nemá, je spočítaný podle metodiky
 *     před první razítkovanou verzí (`METODIKA_PUVODNI`).
 *   · Návrh, který ještě neexistuje (klient bez uloženého Návrhu), se počítá
 *     podle aktuální metodiky — nic se mu měnit nemá, protože nic neviděl.
 *
 * KDO TO ČTE
 *   Solver (`planSolver.solveCile`, volba `metodika`) — rozhoduje se tam, co
 *   která verze počítá jinak. Volby skládá `metodikaDoSolveru(navrh)`; tu
 *   pouští `plneniCilu.solverOptionsZNavrhu` (plán, tisk, prezentace,
 *   1 strana) a obrazovky, které drží rozeditovaný návrh ve stavu (Návrh,
 *   Shrnutí, plán v novém CRM). Bez volby solver počítá aktuální metodikou.
 *
 * PŘÍŠTÍ ZMĚNA METODIKY (třeba přestavba plánu): nová verze do `ZMENY_METODIKY`,
 * `METODIKA_PLANU` na její datum a v solveru/enginu podmínka
 * `platiZmena(metodika, "RRRR-MM-DD")`. Starší plány pak dál počítají postaru
 * a pruh v Návrhu jim nabídne přepočet i s výpisem, co se změní.
 *
 * Modul je schválně BEZ ZÁVISLOSTÍ mimo lib/metodikaVolby (taky bez závislostí;
 * čte ho planSolver, který se načítá i holým node v ověřovacích skriptech).
 */
import { cisteVolby, volbyDoSolveru, SAZBA_HYPO_TRH_PCT, POPLATEK_VYCHOZI_PCT, POPLATEK_ZPUSOB_VYCHOZI } from "./metodikaVolby";

/** Verze Č20 (cíl „hned“ bez měsíční investice, zdroje rezervy „hned“, ruční nula). */
export const METODIKA_C20 = "2026-10-04";

/** Verze 5. 10. 2026 (balík „vše dle doporučení“ 4. 10. 2026): důchod OSVČ
 * (pole „pojištěn/a v ČR od roku“ majitel 4. 10. zrušil — celý život v ČR),
 * invalidita rodiče bez příjmu (varianta B),
 * akontace podle věku v roce koupě, SK LTV podle NBS, ruční částka i v tisku,
 * dospoření rezervy stupňovitě, krok „špatné dluhy“ bez hypotéky; dočištění
 * F3: cíl bez termínu mimo rozdělení, vlastní profil cíle ve výpočtu,
 * navýšení renty v tisku, rezerva jedním číslem (bez zaokrouhlení); dočištění
 * F4: míra odkládání v Návrhu a prezentaci = táž jako v analýze;
 * zajištění příjmu vždy (uložené „Ne“ z analýzy plán nečte — 5. 10. 2026,
 * plneniCilu.ochranaOdmitnuta); splátka úvěru od čerpání krátí peníze pro
 * ostatní cíle a rentu, schodek se hlásí (5. 10. 2026, planSolver
 * `zatizeniUvery`); příspěvek zaměstnavatele v míře odkládání i v příjmu
 * (5. 10. 2026, M1-11, lib/bilance.js); fondy a životko s hodnotou do první
 * renty (5. 10. 2026, planSolver pool `fondy`); chybějící rezerva nejdřív
 * z podílových fondů / ETF / akcií (5. 10. 2026, planSolver
 * `cerpejFondyDoRezervy`). */
export const METODIKA_2026_10_05 = "2026-10-05";

/** Verze 13. 10. 2026: PARTNER BEZ DATA NAROZENÍ (majitel 12. 10. 2026,
 * dotazník „Průvodce — datum partnera“: „Pustit s důvodem (Doporučeno)“ —
 * datum klienta povinné; u partnera důvod a pojištění partnera „nespočteno“).
 * Plán s tímto razítkem partnera bez platného data narození NEPOJIŠŤUJE:
 * smrt, invalidita, nemoc, úraz ani cena se mu nepočítají a výstupy napíšou
 * „nespočteno — chybí datum narození“ (osobyZAnalyzy.nespoctenoPartnera,
 * planEngine `druhaOsobaNespocteno`). Starší plán počítá postaru — čísla,
 * která klient viděl, se nemění (pravidlo 3. 10. 2026). */
export const METODIKA_2026_10_13 = "2026-10-13";

/** Verze 15. 10. 2026: NÁVRH ŘEŠENÍ V 9 KROCÍCH — krok 7 „Stejné peníze dvěma
 * cestami“ (tytéž peníze plánu investované × na spořicím účtu, pásmo horšího
 * a lepšího scénáře, čára po poplatcích; plán se schodkem graf nekreslí).
 * Rozhodnutí 1 a 9 v docs/ZADANI-CUNG.md (F7). Čísla cílů se nemění, jen krok 7
 * a graf s11 klasické prezentace (`platiZmena(verze, METODIKA_2026_10_15)`).
 * AKTIVNÍ od integrace Návrhu 15. 10. 2026 (`METODIKA_PLANU`, záznam
 * `zmenaDveCesty` v ZMENY_METODIKY). Solver na ni nemá žádnou podmínku, takže
 * plán s razítkem 2026-10-13 počítá cíle beze změny (test
 * __tests__/metodika2026_10_15StarePlany.test.js). */
export const METODIKA_2026_10_15 = "2026-10-15";

/** Verze 18. 10. 2026: ALOKACE PODLE TABULEK FICHTNER (majitel 18. 10. 2026
 * k metodice Fichtner: „určitě je to super! jejich metodika“). Cíl s termínem
 * má alokaci podle profilu a počtu let do cíle z tabulky s. 25
 * (lib/alokaceTabulky.js), renta před čerpáním podíl akcií podle profilu
 * a let do čerpání z tabulky s. 27 (lib/rentaPoLetech.js) — místo lineární
 * heuristiky `planSolver.alokaceProCil`. Kapitál renty dál pravidlem 4 %.
 * Solver: `platiZmena(metodika, METODIKA_2026_10_18)` → `tabulky: true`.
 * Plán s razítkem 2026-10-13 počítá na korunu postaru (test
 * __tests__/metodika2026_10_18Fichtner.test.js). Rozhodnutí docs/ZADANI-CUNG.md F9. */
export const METODIKA_2026_10_18 = "2026-10-18";

/** Verze 28. 10. 2026 (majitel klikem, docs/ZADANI-CUNG.md 28. 10.):
 *  1) MOST DO 60 — „Most do 60, penzijko nechat — Renta do 60 z ostatních peněz,
 *     od 60 penzijko bez ztráty státních příspěvků“: penzijko vyplacené až po
 *     začátku renty se nezahodí, od výplaty zvýší rentu o 4 % ročně z jeho hodnoty
 *     (planSolver `c1028`, `doRentyOdVeku`; beta `rentaModelu`). Nic se neruší.
 *  2) POJISTNÉ MAJETKU Z POVINNÝCH PLATEB — „jj život a nemovitosti asi
 *     z povinných plateb no“: cena nové majetkové smlouvy od poradce se
 *     z přebytku odečte celá, ne jen při výměně za zrušenou starou smlouvu
 *     (plneniCilu.pojistneProPlan, lib/nahradaMajetku.js `cenaNovehoMajetku`).
 *  Plán s razítkem 2026-10-18 a starším počítá beze změny. */
export const METODIKA_2026_10_28 = "2026-10-28";

/** Verze 29. 10. 2026 (majitel klikem, docs/ZADANI-CUNG.md 29. 10.): NIŽŠÍ VARIANTY
 *  KRYTÍ snižují i pracovní neschopnost a závažná onemocnění — „Podle nezbytných
 *  výdajů + závazků“: u „Uskromnit se“ a „Řeším jen cenu“ kryje PN jen podíl osoby
 *  na nezbytných výdajích a splátkách (místo celého příjmu), závažná onemocnění
 *  klesnou stejným poměrem jako invalidita osoby (lib/pojisteniNizsiVarianty.js).
 *  Plán s razítkem 2026-10-28 a starším počítá beze změny. */
export const METODIKA_2026_10_29 = "2026-10-29";

/* VRÁCENÉ VERZE (18. 10. 2026): změna verze 2026-10-15 (krok 7 nového Návrhu
 * a graf s11 „dvě cesty“) platí JEN pro plán s razítkem přesně 2026-10-15.
 * Majitel 17. 10. nový Návrh odmítl a razítko vrátil na 2026-10-13; bez téhle
 * výjimky by přepnutí na 2026-10-18 dvě cesty novým plánům potichu zapnulo
 * a pruh by je starým plánům nabízel. */
const VERZE_VRACENE = new Set([METODIKA_2026_10_15]);

/** Razítko AKTUÁLNÍ metodiky — datum verze. Nový návrh dostane tohle.
 * 5. 10. 2026: balík 4. 10. (dráha solveru přepnula po doplnění podmínek
 * `platiZmena(…, METODIKA_2026_10_05)`). Plán s razítkem 2026-10-04 počítá
 * dál podle Č20 a pruh mu nabídne přepočet.
 * 13. 10. 2026: partner bez data narození (METODIKA_2026_10_13). Plán
 * s razítkem 2026-10-05 počítá dál beze změny; pruh v Návrhu se mu ukáže,
 * jen když se ho změna týká (bod `tyka` v ZMENY_METODIKY).
 * 15. 10. 2026: Návrh řešení v 9 krocích — krok 7 „Stejné peníze dvěma cestami“
 * (METODIKA_2026_10_15). Plán s razítkem 2026-10-13 počítá cíle beze změny,
 * jen krok 7 a graf s11 kreslí postaru, dokud poradce nepřepočítá. */
/* 17. 10. 2026 VRÁCENO na 2026-10-13: majitel nový Návrh v 9 krocích odmítl
 * („to řešení plánu je hrozný.. nesmaž hlavně to starý řešení“) a výchozí je
 * zase staré řešení. Konstanta METODIKA_2026_10_15 a záznam zmenaDveCesty
 * zůstávají pro nový Návrh na /navrh-novy, ale razítko nových plánů je 10-13. */
/* 18. 10. 2026: verze METODIKA_2026_10_18 (alokace Fichtner) je v solveru
 * a enginu PŘIPRAVENÁ, ale razítko nových plánů se zatím NEPŘEPÍNÁ: přepnutí
 * mění čísla „aktuální metodiky“, na kterých stojí ~90 testů ve 32 souborech
 * (orákula náhledu, kotva 572 547 / 4 406, Dílna, kina), a odhalilo 3 místa,
 * která musí jít s ním (text renty v doporuceniCile, kinoZdroje, tlačítko
 * pruhu). Přepnutí = tento řádek na METODIKA_2026_10_18 + ta místa
 * (docs/ZADANI-CUNG.md F9). */
/* 19. 10. 2026 PŘEPNUTO na 2026-10-18 (alokace Fichtner): majitel k F9
 * otázkám 1–5: „jo udělej to… a pak v nastavení můžeme změnit.. víc
 * dynamicky, ať to není konzervativní jenom, poradce si pak to tam nastaví“.
 * Nové plány počítají tabulkami s. 25/27, staré plány (razítko 2026-10-13
 * a starší) dál postaru na korunu, pruh jim nabídne přepočet. Volby poradce
 * z Nastavení → Metodika plánu se při vzniku plánu zapíšou do
 * `navrh.metodikaVolby` (lib/metodikaVolby.js). */
/* 28. 10. 2026 PŘEPNUTO na 2026-10-28 (penzijko most do 60, pojistné majetku
 * z povinných plateb — majitel klikem). Plán s razítkem 2026-10-18 počítá
 * dál beze změny, pruh mu nabídne přepočet. */
/* 29. 10. 2026 PŘEPNUTO na 2026-10-29 (nižší varianty krytí snižují i PN a ZO —
 * majitel klikem). Plán s razítkem 2026-10-28 počítá dál beze změny. */
export const METODIKA_PLANU = METODIKA_2026_10_29;

/** Uložený návrh BEZ razítka = počítaný před první razítkovanou verzí. */
export const METODIKA_PUVODNI = "puvodni";

const RX_VERZE = /^\d{4}-\d{2}-\d{2}$/;
const jeObjekt = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Razítko uložené v návrhu, nebo `null` (bez razítka / rozbitá hodnota /
 * návrh neexistuje).
 * @param {object} navrh `data.v2.navrh`
 * @returns {string|null}
 */
export function metodikaPlanu(navrh) {
  if (!jeObjekt(navrh)) return null;
  const v = navrh.metodika;
  return typeof v === "string" && RX_VERZE.test(v) ? v : null;
}

/**
 * Verze, podle které se tenhle návrh POČÍTÁ.
 *   neexistuje (null/undefined)  → aktuální (`METODIKA_PLANU`)
 *   uložený s razítkem           → razítko
 *   uložený bez razítka          → `METODIKA_PUVODNI`
 * @param {object} navrh
 * @returns {string}
 */
export function verzePlanu(navrh) {
  if (!jeObjekt(navrh)) return METODIKA_PLANU;
  return metodikaPlanu(navrh) ?? METODIKA_PUVODNI;
}

/**
 * Je plán počítaný verzí STARŠÍ než `verzeZmeny`?
 * `undefined`/`null`/"" = verze neurčena → aktuální (ne starší). Neplatná
 * hodnota (ani datum, ani „puvodni“) se bere jako stará: nejistota nesmí
 * vést k tomu, že se klientovi tiše změní čísla.
 * @param {string|null|undefined} verze verze plánu (`verzePlanu`)
 * @param {string} verzeZmeny datum verze, ve které změna přišla
 */
export function predVerzi(verze, verzeZmeny) {
  /* Vrácená verze platí jen pro plán s přesně jejím razítkem (VERZE_VRACENE). */
  if (VERZE_VRACENE.has(verzeZmeny)) return verze !== verzeZmeny;
  /* Neurčená verze = aktuální (`METODIKA_PLANU`). Pro verze ≤ aktuální vrací
   * false jako dřív; verze připravená, ale ještě nepřepnutá (2026-10-18), se
   * plánu bez razítka ve volbě nesmí potichu zapnout. */
  if (verze === undefined || verze === null || verze === "") return METODIKA_PLANU < verzeZmeny;
  if (verze === METODIKA_PUVODNI) return true;
  if (typeof verze !== "string" || !RX_VERZE.test(verze)) return true;
  return verze < verzeZmeny;
}

/** Platí pro plán verze `verze` změna z verze `verzeZmeny`? (opak `predVerzi`) */
export function platiZmena(verze, verzeZmeny) {
  return !predVerzi(verze, verzeZmeny);
}

/**
 * Je návrh spočítaný starší metodikou než `verze` (výchozí = aktuální)?
 * Nový (neuložený) návrh NIKDY — ten se počítá aktuálně.
 * @param {object} navrh `data.v2.navrh`
 * @param {string} [verze]
 */
export function jeStaraMetodika(navrh, verze = METODIKA_PLANU) {
  return predVerzi(verzePlanu(navrh), verze);
}

/**
 * Volba solveru pro metodiku návrhu. Aktuální metodika = PRÁZDNÝ objekt, aby
 * nastavení solveru měla u nových plánů týž tvar jako dřív.
 * 19. 10. 2026: plán s razítkem 2026-10-18 a novějším přidá volby poradce
 * zapsané v návrhu (`navrh.metodikaVolby` → `posunLet`, `kapitalRenty`), jen
 * když se liší od výchozích. Starší plán je nedostane nikdy.
 * @param {object} navrh `data.v2.navrh` (u Návrhu a Shrnutí živý stav)
 * @returns {{metodika?: string, posunLet?: number, kapitalRenty?: string}}
 */
export function metodikaDoSolveru(navrh) {
  const v = verzePlanu(navrh);
  /* Návrh, který ještě neexistuje, počítá jako nový (aktuální sazba hypotéky). */
  const volby = platiZmena(v, METODIKA_2026_10_18) ? volbyDoSolveru(jeObjekt(navrh) ? navrh : novyNavrh()) : {};
  return predVerzi(v, METODIKA_PLANU) ? { metodika: v, ...volby } : volby;
}

/**
 * NOVÝ NÁVRH (19. 10. 2026): razítko aktuální metodiky + volby poradce
 * z Nastavení → Metodika plánu, zapsané do návrhu, aby se plán počítal stejně
 * i po pozdější změně nastavení. Výchozí rizikový profil jde do
 * `solver.profil` (odtud ho čte solver i přepínač profilu; kino „Jaký jste
 * investor“ ho tam přepíše).
 * @param {object} [volby] `metodikaVolby.volbyZProfilu(profile)` (camelCase)
 * @returns {{pozn: string, metodika: string, metodikaVolby: object, solver: {profil: string}}}
 */
export function novyNavrh(volby) {
  /* 28. 10. 2026: nový plán dostane aktuální sazbu hypotéky (volby poradce ji
   * zatím nenesou, Nastavení ji nemá — upravuje se u cíle). */
  /* 28. 10. 2026: i vstupní poplatek investičních cílů (2 %, 60/40) — staré plány ho nemají = 0 %. */
  const v = cisteVolby({
    sazbaHypoPct: SAZBA_HYPO_TRH_PCT, vstupniPoplatekPct: POPLATEK_VYCHOZI_PCT, poplatekZpusob: POPLATEK_ZPUSOB_VYCHOZI,
    ...(volby && typeof volby === "object" ? volby : {}),
  });
  return { pozn: "", metodika: METODIKA_PLANU, metodikaVolby: v, solver: { profil: v.profil } };
}

/**
 * Návrh s razítkem (nová kopie). Bez návrhu vznikne `{ metodika }`.
 * @param {object} navrh
 * @param {string} [verze]
 */
export function orazitkuj(navrh, verze = METODIKA_PLANU) {
  return { ...(jeObjekt(navrh) ? navrh : {}), metodika: verze };
}

/**
 * Návrh BEZ razítka (nová kopie) — „Vrátit původní výpočet“.
 * @param {object} navrh
 */
export function bezRazitka(navrh) {
  const kopie = { ...(jeObjekt(navrh) ? navrh : {}) };
  delete kopie.metodika;
  return kopie;
}

/**
 * CO SE OD KTERÉ VERZE ZMĚNILO — pro pruh v Návrhu („co se změní“).
 * Texty jsou česká záloha; obrazovka je překládá klíčem `metodikaPlanu.<klic>`.
 * Jen změny, které HÝBOU ČÍSLY. Čistě vizuální a jazykové opravy téže verze
 * platí pro všechny plány a sem nepatří.
 * `tyka` (volitelné) = kód podmínky, kdy se bod plánu VŮBEC týká (např.
 * „partnerBezNarozeni“). Vyhodnotí ho volající (zmenyOdVerze s `tyka`) —
 * modul je bez závislostí a data analýzy nečte. Bod bez `tyka` platí vždy.
 */
export const ZMENY_METODIKY = [
  {
    verze: METODIKA_C20,
    body: [
      { klic: "zmenaCilHned", cs: "Cíl „hned“ (0 let) se kryje jen z peněz, které klient má — měsíční investici systém sám nepřidá, jen ji nabídne." },
      /* „(0 let)“ (nález ověření 4. 10. 2026): rezerva s horizontem 1 rok a víc
       * bere dál jen účty (zúžení Č20, volba B) — bez upřesnění se věta četla
       * jako „každá rezerva“. */
      { klic: "zmenaZdrojeHned", cs: "Rezerva i cíl s horizontem „hned“ (0 let) berou běžný účet, spořicí účet, hotovost, termínovaný vklad a stavební spoření (v tomto pořadí)." },
      { klic: "zmenaRucniNula", cs: "Smazaná měsíční částka u cíle platí i ve výpočtu — uvolněné peníze dostanou další cíle." },
      /* Drobnost 4. 10. 2026 (dráha S): proč u starého plánu renta „stojí“
       * první rok — rezerva se dorovnává z přebytku do roka (c20-pred-po.md). */
      { klic: "zmenaRenta13", cs: "Starý výpočet posílá rentě peníze až od 13. měsíce — první rok bere celý přebytek dorovnání rezervy." },
    ],
  },
  {
    verze: METODIKA_2026_10_05,
    /* Body doplňují dráhy balíku 4. 10. (každá své), viz komentář u konstanty. */
    body: [
      /* Dráha P1 (invalidní důchod, lib/socialDavky.js + lib/planEngine.js). */
      /* 4. 10. 2026 ~22:50: majitel zrušil pole „V Česku pojištěn/a od roku“
       * („Úplně odstranit“) — z bodu zmizela „kratší doba u lidí, kteří přišli
       * do Česka v dospělosti“; počítá se celý život v ČR. */
      { klic: "zmenaInvalidniDuchod", cs: "Invalidní důchod od státu: u osoby s příjmem průměr (v Česku 13 000 Kč měsíčně), poradce ho na kartě přepíše — ne výpočet ze mzdy. Bez nároku 0, bez příjmu zákonné minimum." },
      { klic: "zmenaInvaliditaBezPrijmu", cs: "Rodič bez příjmu má krytí invalidity jen na o 20 % vyšší výdaje domácnosti mínus invalidní důchod, pokud na něj má nárok — stejně jako první i druhá osoba." },
      /* Dráha F1 (dočištění 4. 10. 2026, lib/invaliditaBezPrijmu.js). */
      { klic: "zmenaInvaliditaNikdoNevydelava", cs: "Když v domácnosti nikdo nevydělává, krytí invalidity se nepočítá a plán napíše „bez příjmu – krytí invalidity nepočítáme, doplňte příjem“." },
      { klic: "zmenaInvaliditaNulaTisk", cs: "Rodič bez příjmu, kterému invalidní důchod pokryje o 20 % vyšší výdaje domácnosti, má v Plném plánu i na 1 straně u invalidity 0 Kč s důvodem (dřív „—“)." },
      /* Dráha S (akontace, solver, tisk). */
      { klic: "zmenaAkontaceVek", cs: "Akontace 10 % (výjimka pro mladé) se posuzuje podle věku v roce koupě (dnešní věk + roky do cíle), ne podle dnešního věku." },
      { klic: "zmenaAkontaceSK", cs: "Slovensko: akontace podle NBS — při koupi do konce roku 2026 jistých 80 % LTV, od roku 2027 stačí 10 % jen při prvním bydlení: každému žadateli nejvýš 35 let (druhý klient bez data narození podmínku nesplní), žádný byt ani dům k bydlení ani k pronájmu a žádná hypotéka v závazcích." },
      { klic: "zmenaRucniCastka", cs: "Ručně zadaná měsíční částka u cíle platí i ve výpočtu — tiskne se do Plného plánu, na 1 stranu, do Shrnutí i prezentace a další cíle dostanou zbytek." },
      { klic: "zmenaDosporeniStupne", cs: "Po dospoření rezervy za zvolený počet měsíců dostanou další cíle uvolněné peníze od konce dospoření — stupňovitě, ne nejmenší částku na celou dobu. Navýšení i rok, od kdy platí, se tiskne do Plného plánu a na 1 stranu." },
      { klic: "zmenaSpatneDluhy", cs: "Krok „Zbavit se špatných dluhů“ počítá jen úvěry v záložce Osobní úvěry — spotřebitelské úvěry, kreditky, kontokorenty, revolving, osobní leasing a starší řádky bez zvoleného druhu. Hypotéka ani úvěry v záložce Firemní úvěry mezi špatné dluhy nepatří." },
      /* Dráha F3 (dočištění 4. 10. 2026, čísla plánu — planSolver, plneniCilu,
       * planEngine, shrnutiPlanu). */
      { klic: "zmenaCilBezTerminu", cs: "Cíl bez termínu nedostane peníze z účtů ani měsíční částku — výstupy napíšou „doplňte termín“ a peníze zůstanou cílům s termínem." },
      { klic: "zmenaProfilCile", cs: "Vlastní rizikový profil cíle platí i ve výpočtu — karta cíle i všechny výstupy ukazují u cíle stejné procento." },
      { klic: "zmenaRentaNavyseni", cs: "Navýšení renty z peněz uvolněných po splnění dřívějších cílů se tiskne do Plného plánu a na 1 stranu i s rokem, od kdy platí — jako u ostatních cílů." },
      { klic: "zmenaRezervaJednoCislo", cs: "Rezerva má na celém plánu jedno číslo: cíl přesně podle vzorce z analýzy (bez zaokrouhlení po 10 000) a co klient má, kolik chybí a na kolik procent je naplněná, počítá jedna funkce — i se stavebním spořením, které dorazí do ~4 měsíců." },
      /* Dráha M (Č19, pojištění majetku po smlouvách — lib/majetekSmlouvy.js). */
      { klic: "zmenaMajetekSmlouvy", cs: "Pojištění majetku se v plánu tiskne po smlouvách a jen s cenou z nabídky pojišťovny; orientační odhad zůstává jen poradci na obrazovce." },
      /* Dráha F4 (jedna míra odkládání — lib/bilance.js `miraOdkladani`,
       * `jednaMiraVPlanu`; Návrh/rozpočet `rozpocetPlanu.miraUspor`,
       * prezentace `planEngine.miraOdkladani`). */
      { klic: "zmenaMiraOdkladani", cs: "Míra odkládání v Návrhu, v rozpočtu plánu i v prezentaci je stejné číslo jako v analýze: odsouhlasená částka (bez ní volné peníze z příjmů a výdajů) plus pravidelné vklady na spoření a investice, děleno čistým příjmem. Dřív jen přebytek po pojistném děleno příjmem, bez vkladů." },
      /* Příspěvek zaměstnavatele (nález M1-11, majitel 5. 10. 2026 „ano“) —
       * lib/bilance.js `miraOdkladaniZCisel` (`zamestnavatel`, `zaklad`). */
      { klic: "zmenaZamestnavatel", cs: "Příspěvek zaměstnavatele se počítá do odkládání i do příjmu: míra odkládání = (odsouhlasená částka nebo volné peníze + vlastní vklady + příspěvek zaměstnavatele) děleno (čistý příjem + příspěvek zaměstnavatele). Dřív jen do odkládání, takže míra vyšla o kus vyšší." },
      /* Dráha F2 (dočištění 4. 10. 2026): odhad hrubé z čisté a cena po změně
       * pojistné částky už do tisku nejdou (tiskne se jen po smlouvách, tedy
       * jen plán s tímto razítkem). */
      { klic: "zmenaMajetekMzda", cs: "Odpovědnost zaměstnance se tiskne s limitem jen z hrubé mzdy, kterou poradce zadal nebo potvrdil — odhad z čisté mzdy zůstává poradci na obrazovce a v plánu stojí „limit po doplnění mzdy“. Cena pojištění majetku po změně pojistné částky do součtu ani do tisku nejde, dokud ji poradce znovu nepotvrdí." },
      /* Rizika bez otázky (5. 10. 2026, majitel: „tohle neni potreba“):
       * otázka „Dává vám smysl řešit zajištění příjmu?“ odešla z analýzy,
       * uložené „Ne“ čte jen plán starší než tahle verze
       * (plneniCilu.ochranaOdmitnuta). */
      { klic: "zmenaZajisteniVzdy", cs: "Zajištění příjmu plán řeší vždy — dřívější „Ne“ klienta z analýzy už pojistné z přebytku nevyřadí." },
      /* Smrt bez minima (majitel 5. 10. 2026: „používej naši předchozí
       * metodiku“) — planEngine `smrtSMinimem`. */
      { klic: "zmenaSmrtBezMinima", cs: "Krytí smrti ve velké analýze jen kapitálovou metodou, bez minima (minimum jen v Analýze pro Vietnamce)." },
      /* Splátka od čerpání (majitel 5. 10. 2026: „a pak zbytek kolik bude
       * splátka“) — planSolver `zatizeniUvery`, `schodekUveru`. Nájem jen
       * z položky Nájem v rozpisu výdajů (bod 9 dráhy splátky, `najemPredKoupi`). */
      { klic: "zmenaSplatkaUveru", cs: "Po čerpání úvěru se splátka odečte z peněz pro ostatní cíle a rentu (u bydlení se přičte nájem z rozpisu výdajů, který koupí odpadne). Když splátka přesáhne rozpočet, plán napíše, od kdy a kolik chybí." },
      /* Záporný přebytek a růst mzdy (bod 8 dráhy splátky 5. 10. 2026) —
       * planSolver `volnePredCili` (kapacita i graf „Cashflow v čase“). */
      { klic: "zmenaRustMzdySchodek", cs: "Když rodina dnes utratí víc, než vydělá, růst mzdy nejdřív pokryje tenhle schodek — cíle a splátka úvěru dostanou až to, co zbude. Dřív šel celý růst mzdy na cíle." },
      /* Karta cíle 5. 10. 2026 (majitel: „u auta se řeší úvěrem spotřebitelským
       * nebo ze stavebka … do 12 let max“) — planSolver `uverVarianty`. */
      { klic: "zmenaUverSpotrebitelsky", cs: "Auto a další cíle na úvěr mimo bydlení počítají splátku spotřebitelského úvěru nebo úvěru ze stavebního spoření (splatnost nejvýš 12 let, orientační sazba), ne hypotéky na 30 let." },
      /* Fondy do renty (majitel 5. 10. 2026: „Ano, do renty“) — planSolver
       * pool `fondy`, `LABEL_FONDY`; rozpočet `fondyCerpanoZeSolveru`. */
      { klic: "zmenaFondyDoRenty", cs: "Podílové fondy, akcie a životko s hodnotou (odkupné), které klient už má, jdou do první renty — jen jednou, i když jsou renty dvě — a do jejího startu rostou podle profilu renty. Poradce je může v toku peněz poslat na jiný cíl. Dřív je plán nepřiřadil k žádnému cíli." },
      /* Rezerva nejdřív z fondů (majitel 5. 10. 2026, dotazník: „Nejdřív doplnit
       * rezervu“) — planSolver `cerpejFondyDoRezervy`, `LABEL_FONDY_REZERVA`;
       * Návrh a papír krok „doplní se z fondů X“ (`stavRezervy.zFondu`). */
      { klic: "zmenaFondyDoRezervy", cs: "Když klientovi chybí rezerva a má podílové fondy, ETF nebo akcie, plán z nich nejdřív doplní chybějící část rezervy (jen tu, nikdy víc) a až zbytek fondů jde do renty. Investiční životko do rezervy nejde — odkupné je až po výpovědi smlouvy. Dřív šly všechny fondy do renty a rezerva čekala na dospoření z přebytku." },
      /* Potřeba renty (majitel 5. 10. 2026: „Ano, počítat bez nich“, M1-15) —
       * planEngine `rentaPotreba` přes lib/analyzaV2/potrebaRenty.js. Analýza
       * (panel, nápověda u cíle, kina) to má hned, plán jen od tohoto razítka. */
      { klic: "zmenaPotrebaRenty", cs: "Renta bez zadaného cíle se počítá jako v analýze: dnešní výdaje bez vlastních vkladů na spoření plus splátky úvěrů, které poběží i v rentě. Hypotéka doplacená před rentou (podle data konce, jinak odhadem z jistiny, úroku a splátky) se nepočítá, ostatní úvěry zůstávají. Dřív renta = celé dnešní výdaje bez splátek." },
      /* Velká kontrola 4.–5. 10. 2026, dráha čísla: M2-02 + M2-06 (lib/socialDavky.js
       * `hrubaZCisteCZ`, nárok mladých; starší plán `invaliditaPostaru`). */
      { klic: "zmenaHrubaZCiste", cs: "Nemocenská (pracovní neschopnost) se počítá z hrubé mzdy dopočtené z čisté podle daní a pojistného 2026 (po pásmech), ne z čisté × 1,39. Výdělečný člověk mladší 22 let má na invalidní důchod nárok." },
      /* M1-12 — planEngine `majetkovaZadana`. */
      { klic: "zmenaSkoreZajisteni", cs: "Skóre finančního zdraví nepočítá prázdné řádky majetkových rizik jako zadané zajištění — bez zadané částky se tahle složka skóre nepočítá (dřív 20 bodů za prázdnou šablonu)." },
      /* M1-07 — pages/planPrezentace/grafy.js `krivkaSrovnani` (c.verzeMetodiky). */
      { klic: "zmenaSrovnaniStejnyZaklad", cs: "Graf „bez plánu a s plánem“ v prezentaci začíná obě křivky ze stejného finančního majetku; nezapojený majetek roste stejně jako zapojený a inflace je 2,5 % z metodiky (dřív zelená začínala jen z peněz přidělených cílům a červená počítala 2,7 %)." },
      /* M1-02 — planSolver penzijko z poolu a `pritokyDoRenty`. */
      { klic: "zmenaPenzijkoJednou", cs: "Penzijko a jednorázový příjem bez určeného cíle dostane jen první renta v pořadí — dvě renty si už tutéž korunu nerozdají dvakrát." },
      /* Průchod Návrhem 5. 10. 2026, bod 2 — planSolver `chybiNaOchranu`
       * (graf, nápis o schodku, souhrn `vRozpoctuMes` → rozpocetPlanu). */
      { klic: "zmenaOchranaNadPrebytek", cs: "Když je ochrana dražší než přebytek, chybějící částka je vidět v grafu „Cashflow v čase“, v nápisu o schodku, v rozpočtu plánu, v Průběžném přehledu, v prezentaci i ve Shrnutí. Dřív se pojistné ořízlo na výši přebytku a chybějící peníze se ztratily." },
      /* Průchod Návrhem 5. 10. 2026, bod 3 — lib/nahradaMajetku.js
       * (plneniCilu.pojistneProPlan). */
      { klic: "zmenaMajetekNahrada", cs: "Stávající pojištění majetku (domácnost, nemovitost, odpovědnost) se v plánu uvolní, až když má nová majetková smlouva cenu od poradce — pak plán počítá výměnu celou: starou platbu uvolní a cenu nové odečte. Dřív se zrušení počítalo jako uvolněné peníze spolu s životní smlouvou, i když cena nové smlouvy chyběla." },
    ],
  },
  {
    verze: METODIKA_2026_10_13,
    body: [
      /* Majitel 12. 10. 2026 (dotazník „Průvodce — datum partnera“) —
       * osobyZAnalyzy.nespoctenoPartnera, planEngine `druhaOsobaNespocteno`. */
      { klic: "zmenaPartnerBezNarozeni", tyka: "partnerBezNarozeni", cs: "Partner bez data narození: jeho zajištění plán nespočítá (smrt, invalidita, nemoc, úraz ani cenu) a výstupy napíšou „nespočteno — chybí datum narození“. Jeho stávající smlouvy plán neruší. Dřív se krytí partnera počítalo i bez data narození." },
    ],
  },
  {
    verze: METODIKA_2026_10_15,
    body: [
      /* Rozhodnutí 1 a 9 (docs/ZADANI-CUNG.md F7) — lib/navrh/krivkaPlanu,
       * krok 7 Návrhu a snímek s11 klasické prezentace. Čísla cílů beze změny. */
      { klic: "zmenaDveCesty", cs: "Graf „Bez plánu a s plánem“ porovnává tytéž peníze plánu investované a na spořicím účtu, s pásmem horšího a lepšího scénáře a čarou po poplatcích; plán se schodkem graf nekreslí. Dřív graf investoval celý majetek bez výběrů na cíle." },
    ],
  },
  {
    verze: METODIKA_2026_10_18,
    body: [
      /* Majitel 18. 10. 2026 (metodika Fichtner, s. 25 a 27) — lib/alokaceTabulky,
       * lib/rentaPoLetech, planSolver.alokaceProCil `tabulky`. */
      { klic: "zmenaAlokaceFichtner", cs: "Rozložení peněz cíle (akcie, dluhopisy, hotovost) se řídí tabulkou podle rizikového profilu a počtu let do cíle, ne plynulým odprodejem akcií. Poslední rok před cílem drží 97 % v hotovosti a 3 % v dluhopisech (u dynamického profilu 71 % a 29 %). Renta má před čerpáním podíl akcií podle profilu a let do začátku čerpání (vyvážený profil 15 a víc let 88 %), dřív se blížila 50 % akcií. Měsíční částky cílů se proto posunou: u konzervativního a vyváženého profilu krátké cíle mírně zdraží, renta u vyváženého a dynamického profilu vyjde levněji." },
    ],
  },
  {
    verze: METODIKA_2026_10_28,
    body: [
      /* Majitel 28. 10. 2026 — planSolver `c1028` (penzijko most do 60), plneniCilu.pojistneProPlan (majetek). */
      { klic: "zmenaPenzijkoDoRenty", tyka: "penzijko", cs: "Penzijko se neruší: když renta začíná dřív než výplata penzijka (v Česku 60 let), renta do výplaty jde z ostatních peněz a od výplaty se zvýší o penzijko (4 % ročně z jeho hodnoty) — bez ztráty státních příspěvků. Dřív se penzijko vyplacené po začátku renty do renty nepočítalo vůbec." },
      { klic: "zmenaPojistneMajetku", tyka: "cenaMajetku", cs: "Cena nového pojištění majetku (domácnost, nemovitost, odpovědnost) od poradce patří mezi povinné platby a odečte se z přebytku celá, stejně jako životní pojištění. Zrušená stará majetková smlouva se dál vrací jako úspora. Dřív se odečítal jen rozdíl při výměně za zrušenou smlouvu." },
    ],
  },
  {
    verze: METODIKA_2026_10_29,
    body: [
      /* Majitel 29. 10. 2026 — lib/pojisteniNizsiVarianty.js, pojisteniEngine `pnVydajeMes`, `zavazneKoef`. */
      { klic: "zmenaNizsiVarianty", tyka: "nizsiVarianty", cs: "Varianty „Uskromnit se“ a „Řeším jen cenu“ snižují i pracovní neschopnost a závažná onemocnění: denní dávka kryje jen nezbytné výdaje a splátky (místo celého příjmu), závažná onemocnění klesnou stejným poměrem jako invalidita. Dřív zůstávaly jako u varianty „Pro náročné“." },
    ],
  },
];

/**
 * Body změn, které se plánu verze `verze` týkají (verze novější než plán).
 * @param {string} verze verze plánu (`verzePlanu`)
 * @param {{ tyka?: (kod: string) => boolean }} [o] `tyka` = platí podmínka
 *   bodu u tohoto klienta? (bez ní se vrátí všechny body, jako dřív)
 * @returns {Array<{verze: string, klic: string, cs: string}>}
 */
export function zmenyOdVerze(verze, { tyka } = {}) {
  const out = [];
  for (const z of ZMENY_METODIKY) {
    if (!predVerzi(verze, z.verze)) continue;
    /* 18. 10. 2026: verze novější než aktuální (2026-10-15 po vrácení 17. 10.)
     * přepočet nepřinese — pruh ji nesmí slibovat. */
    if (predVerzi(METODIKA_PLANU, z.verze)) continue;
    for (const b of z.body) {
      if (b.tyka && typeof tyka === "function" && !tyka(b.tyka)) continue;
      out.push({ verze: z.verze, klic: b.klic, cs: b.cs });
    }
  }
  return out;
}

/** „2026-10-04“ → „4. 10. 2026“ (datum verze pro větu v pruhu). */
export function datumVerze(verze) {
  if (typeof verze !== "string" || !RX_VERZE.test(verze)) return "";
  const [r, m, d] = verze.split("-").map((x) => parseInt(x, 10));
  return `${d}. ${m}. ${r}`;
}
